import { isEmpty, isNullish } from "@bodil/core/assert";
import {
    attribute,
    attributeGetter,
    Component,
    reactive,
    type Deps,
    type Emits,
} from "@bodil/dom/component";
import { findAncestor, HasSlotController } from "@bodil/dom/dom";
import { mdiChevronRight } from "@mdi/js";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";

import { RippleController } from "./controllers/ripple";
import { BtkIcon, type BtkIconName } from "./icon";
import { KeyboardCommand, keymapLookup } from "./keymap";
import { BtkMenu } from "./menu";
import { BtkPopup } from "./popup";

import styles from "./menu-item.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "borkbot-menu-item": BtkMenuItem;
    }
}

@customElement("btk-menu-item")
export class BtkMenuItem extends Component {
    emits!: Emits<"btk-selected">;

    static deps: Deps = [BtkIcon, BtkPopup];

    @attribute accessor icon: BtkIconName | null = null;
    @attribute({ type: Boolean }) accessor disabled = false;

    @attributeGetter get role(): Role {
        return "menuitem";
    }

    @reactive iconTemplate?: unknown;

    get submenus(): Array<Element> {
        return this.querySlot({ slot: "submenu", flatten: true });
    }

    private readonly submenuPopup: Ref<BtkPopup> = createRef();
    private readonly rippleController = new RippleController(this);

    private get hasSubmenu(): boolean {
        return this.submenuPopup.value !== undefined;
    }

    private readonly hasSlotController = new HasSlotController(this, "submenu");

    protected override connected() {
        this.useWhileConnected(
            this.on("mouseover", (e) => {
                this.focus();
                findAncestor(this, (el) => el instanceof BtkMenu)?.setCurrentItem(this);
                e.stopPropagation();
            }),
            this.on("focus", () => {
                if (this.hasSubmenu) {
                    void this.submenuPopup.value!.show();
                }
            }),
            this.on("focusout", (e) => {
                if (this.hasSubmenu) {
                    if (e.relatedTarget instanceof Element && this.contains(e.relatedTarget)) {
                        return;
                    }
                    void this.submenuPopup.value!.hide();
                }
            }),
            this.on("click", this.handleClick.bind(this)),
            this.on("keydown", this.handleKeyDown.bind(this)),
            this.on("mousedown", this.handleMouseDown.bind(this)),
        );
    }

    private handleMouseDown(event: MouseEvent) {
        if (!this.disabled) {
            this.rippleController.playRipple(this, event);
        }
    }

    private handleClick(e: MouseEvent) {
        if (e.target === this) {
            if (this.disabled) {
                e.preventDefault();
                e.stopImmediatePropagation();
            } else if (this.hasSubmenu) {
                e.preventDefault();
                e.stopPropagation();
                void this.submenuPopup.value!.show();
            } else {
                this.emit("btk-selected");
            }
        }
    }

    private handleKeyDown(e: KeyboardEvent) {
        let cmd = keymapLookup(e);
        const submenuRect = this.submenuPopup.value?.getPopupRect();
        function flipControls(cmd: KeyboardCommand | undefined): KeyboardCommand | undefined {
            if (cmd === KeyboardCommand.Right) {
                return KeyboardCommand.Left;
            }
            if (cmd === KeyboardCommand.Left) {
                return KeyboardCommand.Right;
            }
            return cmd;
        }
        if (this.hasSubmenu && submenuRect !== undefined) {
            if (submenuRect.left < this.getBoundingClientRect().left) {
                this.setAttribute("data-menu-flipped-controls", "true");
                cmd = flipControls(cmd);
            } else {
                this.removeAttribute("data-menu-flipped-controls");
            }
        }
        const parentMenuItem = findAncestor(this, (el) => el instanceof BtkMenuItem);
        if (parentMenuItem?.getAttribute("data-menu-flipped-controls") === "true") {
            cmd = flipControls(cmd);
        }
        switch (cmd) {
            case KeyboardCommand.Right:
            case KeyboardCommand.Enter: {
                if (this.hasSubmenu) {
                    e.stopPropagation();
                    e.preventDefault();
                    let submenu = this.submenus[0];
                    if (!isNullish(submenu) && !(submenu instanceof BtkMenu)) {
                        // Accept a submenu that's a custom element wrapping
                        // a btk-menu.
                        const shadowRoot = submenu.shadowRoot;
                        if (!isNullish(shadowRoot)) {
                            const firstChild = shadowRoot.firstElementChild;
                            if (firstChild instanceof BtkMenu) {
                                submenu = firstChild;
                            }
                        }
                    }
                    if (e.target === this && submenu instanceof BtkMenu) {
                        if (submenu.getCurrentItem() === undefined) {
                            submenu.activateFirstItem();
                        }
                        submenu.getCurrentItem()?.focus();
                    }
                }
                break;
            }
            case KeyboardCommand.Left:
            case KeyboardCommand.Leave: {
                if (e.target !== this) {
                    e.stopPropagation();
                    e.preventDefault();
                    this.focus();
                }
                break;
            }
            default:
                break;
        }
    }

    protected override render(): unknown {
        return html`
            <slot name="prefix">
                ${when(
                    !isEmpty(this.icon) && isNullish(this.iconTemplate),
                    () => html`<btk-icon size="large" name=${ifDefined(this.icon)}></btk-icon>`,
                )}
                ${when(!isNullish(this.iconTemplate), () => this.iconTemplate)}
            </slot>
            <slot></slot>
            ${when(this.hasSlotController.test("submenu"), () =>
                this.disabled
                    ? html`<btk-icon path=${mdiChevronRight}></btk-icon>`
                    : html`
                          <btk-popup
                              ${ref(this.submenuPopup)}
                              .anchor=${this}
                              placement="right-start"
                              skidding="-9"
                              flip
                              shift
                          >
                              <btk-icon slot="anchor" path=${mdiChevronRight}></btk-icon>
                              <slot name="submenu"></slot>
                          </btk-popup>
                      `,
            )}
        `;
    }

    static styles = styles;
}
